import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchManualsByMasterAssembly, updateManual } from "../../apiServices/manualsApi";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { searchParts } from "../../apiServices/partsSearchApi";
import type { ManualDto } from "../../models/Manual";
import type { PartSearchResult } from "../../models/PartSearch";
import { useUserStore } from "../../store/userStore";
import "./EditManualTitle.css";

type LocationState = {
  manual?: ManualDto;
  masterAssemblyId?: number;
};

const EditManualTitle: React.FC = () => {
  const { manualId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore((s) => s.user);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const state = (location.state || {}) as LocationState;
  const initialManual = state.manual;
  const masterAssemblyId =
    state.masterAssemblyId ??
    (location.search ? Number(new URLSearchParams(location.search).get("masterAssemblyId")) : undefined);

  const [title, setTitle] = useState(initialManual?.title ?? "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reuseManualId, setReuseManualId] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [partResults, setPartResults] = useState<PartSearchResult[]>([]);
  const [manualResults, setManualResults] = useState<ManualDto[]>([]);
  const [selectedPart, setSelectedPart] = useState<PartSearchResult | null>(null);
  const [selectedManual, setSelectedManual] = useState<ManualDto | null>(null);

  const targetManualId = useMemo(() => Number(manualId), [manualId]);
  const currentMasterId = initialManual?.masterAssemblyId;

  const handleSearchParts = async () => {
    setError(null);
    setMessage(null);
    setSelectedPart(null);
    setManualResults([]);
    setSelectedManual(null);
    try {
      const results = await searchParts(oem, searchKeyword);
      setPartResults(results);
    } catch (err: any) {
      setError(err?.message || "Failed to search parts");
    }
  };

  const handleSelectPart = async (part: PartSearchResult) => {
    setError(null);
    setMessage(null);
    setSelectedManual(null);
    setManualResults([]);
    setSelectedPart(part);
    try {
      let partMasterId = part.masterAssemblyId;
      if (!partMasterId) {
        const master = await masterAssemblyApi.getByOEMAndPartNumber(
          oem,
          part.componentPartNumber
        );
        partMasterId = master.masterAssemblyId;
      }
      if (currentMasterId && partMasterId === currentMasterId) {
        setError("Reference manual cannot be from the same part number.");
        return;
      }
      const manuals = await fetchManualsByMasterAssembly(oem, partMasterId);
      setManualResults(manuals);
    } catch (err: any) {
      setError(err?.message || "Failed to load manuals for selected part");
    }
  };

  const handleSelectManual = (manual: ManualDto) => {
    setSelectedManual(manual);
    setReuseManualId(String(manual.manualId));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialManual || !targetManualId) {
      setError("Manual details are missing.");
      return;
    }
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const reuseIdNum = reuseManualId ? Number(reuseManualId) : undefined;
      await updateManual(
        oem,
        {
          manualId: targetManualId,
          masterAssemblyId: initialManual.masterAssemblyId,
          manualType: initialManual.manualType,
          title: title.trim(),
          status: initialManual.status,
          reviewerComments: initialManual.reviewerComments ?? null,
          version: initialManual.version,
          createdOn: initialManual.createdOn,
          updatedOn: new Date().toISOString(),
          createdBy: initialManual.createdBy,
          updatedBy: user?.username || "unknown",
        },
        reuseIdNum
      );
      setMessage("Manual updated.");
      if (masterAssemblyId) {
        navigate(`/authoring/view/${masterAssemblyId}`);
      } else {
        navigate(-1);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to update manual");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-manual-title">
      <div className="edit-manual-title__card">
        <div className="edit-manual-title__header">
          <h2>Edit Manual Title</h2>
        </div>
        <form className="edit-manual-title__body" onSubmit={handleSave}>
          <div className="edit-manual-title__field">
            <label>Manual ID</label>
            <input value={initialManual?.manualId ?? targetManualId ?? ""} disabled />
          </div>
          <div className="edit-manual-title__field">
            <label>Manual Type</label>
            <input value={initialManual?.manualType ?? ""} disabled />
          </div>
          <div className="edit-manual-title__field">
            <label>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="edit-manual-title__field">
            <label>Reuse Manual ID (optional)</label>
            <input
              value={reuseManualId}
              onChange={(e) => setReuseManualId(e.target.value)}
              placeholder="Manual ID to reuse content"
            />
          </div>

          <div className="edit-manual-title__divider">Or select a reference manual</div>

          <div className="edit-manual-title__search">
            <input
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchParts();
                }
              }}
              placeholder="Search part number or description"
            />
            <button type="button" className="btn-primary" onClick={handleSearchParts}>
              Search
            </button>
          </div>

          {partResults.length > 0 && (
            <div className="edit-manual-title__results">
              <div className="edit-manual-title__results-title">Matching Parts</div>
              {partResults.map((part) => (
                <button
                  type="button"
                  key={`${part.componentPartNumber}-${part.masterAssemblyId ?? "x"}`}
                  className={`edit-manual-title__result ${
                    selectedPart?.componentPartNumber === part.componentPartNumber ? "active" : ""
                  }`}
                  onClick={() => handleSelectPart(part)}
                >
                  <div className="edit-manual-title__result-main">{part.componentPartNumber}</div>
                  <div className="edit-manual-title__result-sub">
                    {part.componentDescription || "No description"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedPart && (
            <div className="edit-manual-title__results">
              <div className="edit-manual-title__results-title">
                Manuals for {selectedPart.componentPartNumber}
              </div>
              {manualResults.length > 0 ? (
                manualResults.map((manual) => (
                  <button
                    type="button"
                    key={manual.manualId}
                    className={`edit-manual-title__result ${
                      selectedManual?.manualId === manual.manualId ? "active" : ""
                    }`}
                    onClick={() => handleSelectManual(manual)}
                  >
                    <div className="edit-manual-title__result-main">
                      {manual.manualType} - {manual.title}
                    </div>
                    <div className="edit-manual-title__result-sub">
                      Manual ID: {manual.manualId}
                    </div>
                  </button>
                ))
              ) : (
                <div className="edit-manual-title__empty">No manuals found for this part.</div>
              )}
            </div>
          )}

          {error && <div className="edit-manual-title__message error">{error}</div>}
          {message && <div className="edit-manual-title__message success">{message}</div>}

          <div className="edit-manual-title__footer">
            <button type="button" className="btn-outline" onClick={() => navigate(-1)} disabled={loading}>
              Back
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditManualTitle;
